import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GridColumn } from '../grid.models';
import { formatKlDateCaption, formatKlDateTimeCaption } from '../../../utils/kl-date-format';

export interface ColumnFilterState {
  binding: string;
  sortDirection: 'asc' | 'desc' | null;
  checkedValues: Set<unknown>;
  active: boolean;
}

interface FilterValueItem {
  raw: unknown;
  caption: string;
  checked: boolean;
}

@Component({
  selector: 'kl-column-filter',
  standalone: false,
  templateUrl: './kl-column-filter.html',
  styleUrl: './kl-column-filter.scss',
})
export class KlColumnFilter implements OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly el = inject(ElementRef);

  readonly column = input.required<GridColumn>();
  readonly data = input.required<any[]>();
  readonly filterState = input<ColumnFilterState | null>(null);
  /** Asc/desc actions in the popover. */
  readonly showSort = input(true);
  /** Value checklist filter (client-side on current rows). */
  readonly showValueFilter = input(true);

  readonly sortAsc = output<string>();
  readonly sortDesc = output<string>();
  readonly filterApply = output<ColumnFilterState>();
  readonly filterClear = output<string>();

  readonly isOpen = signal(false);
  readonly searchText = signal('');
  readonly selectAllChecked = signal(true);

  readonly panelTop = signal(0);
  readonly panelLeft = signal(0);

  readonly valueItems = signal<FilterValueItem[]>([]);
  private readonly allValueItems = signal<FilterValueItem[]>([]);
  /** Set once the user types their first search term, so that keystroke can clear the default "all checked" selection. */
  private readonly searchModeActive = signal(false);

  readonly triggerRef = viewChild<ElementRef<HTMLButtonElement>>('triggerBtn');
  private readonly panelRef = viewChild<ElementRef<HTMLElement>>('filterPanel');

  /** The panel node once re-parented to `<body>` (see {@link detachPanelToBody}). */
  private movedPanel: HTMLElement | null = null;

  /**
   * The one panel currently open. The grid header stops `mousedown` propagation (for column
   * drag/sort), so clicking another column's trigger never reaches an open panel's
   * `document:mousedown` handler — opening one explicitly closes the previous instead.
   */
  private static openPanel: KlColumnFilter | null = null;

  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const target = event.target as HTMLElement;
    if (!this.el.nativeElement.contains(target) && !this.movedPanel?.contains(target)) {
      this.close();
    }
  }

  ngOnDestroy(): void {
    if (KlColumnFilter.openPanel === this) {
      KlColumnFilter.openPanel = null;
    }
    this.movedPanel?.remove();
  }

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (KlColumnFilter.openPanel && KlColumnFilter.openPanel !== this) {
      KlColumnFilter.openPanel.close();
    }
    KlColumnFilter.openPanel = this;

    this.detachPanelToBody();
    if (this.showValueFilter()) {
      this.buildValueList();
      this.searchText.set('');
      this.searchModeActive.set(false);
    }
    this.computePanelPosition();
    this.isOpen.set(true);
    this.cdr.markForCheck();
  }

  /**
   * Move the panel out to `<body>`. Once the grid crosses its row threshold it turns on the
   * virtual scroller, whose `transform` on an ancestor would otherwise become the containing
   * block for this `position: fixed` panel and throw its viewport-based coordinates off.
   * Re-parenting to `<body>` keeps it anchored under the trigger button. Runs once, on the
   * first open; Angular keeps binding/rendering the node from its original view location.
   */
  private detachPanelToBody(): void {
    if (this.movedPanel) return;
    const panel = this.panelRef()?.nativeElement;
    if (panel) {
      document.body.appendChild(panel);
      this.movedPanel = panel;
    }
  }

  close(): void {
    if (KlColumnFilter.openPanel === this) {
      KlColumnFilter.openPanel = null;
    }
    this.isOpen.set(false);
    this.cdr.markForCheck();
  }

  onSortAsc(): void {
    this.sortAsc.emit(this.column().binding!);
    this.close();
  }

  onSortDesc(): void {
    this.sortDesc.emit(this.column().binding!);
    this.close();
  }

  onSearchChange(text: string): void {
    this.searchText.set(text);
    const term = text.toLowerCase().trim();

    // First keystroke of a search clears the default "all checked" state, turning the
    // search into a deliberate pick rather than a view into a stale select-all.
    if (term && !this.searchModeActive()) {
      this.searchModeActive.set(true);
      this.allValueItems.set(this.allValueItems().map((item) => ({ ...item, checked: false })));
    }

    if (!term) {
      this.valueItems.set([...this.allValueItems()]);
    } else {
      this.valueItems.set(this.allValueItems().filter((item) => item.caption.toLowerCase().includes(term)));
    }
    this.updateSelectAllState();
    this.cdr.markForCheck();
  }

  onSelectAllChange(checked: boolean): void {
    this.selectAllChecked.set(checked);
    const visibleRaws = new Set(this.valueItems().map((item) => item.raw));
    const updated = this.valueItems().map((item) => ({ ...item, checked }));
    this.valueItems.set(updated);
    const allUpdated = this.allValueItems().map((v) => (visibleRaws.has(v.raw) ? { ...v, checked } : v));
    this.allValueItems.set(allUpdated);
    this.cdr.markForCheck();
  }

  onItemChange(item: FilterValueItem, checked: boolean): void {
    const updated = this.valueItems().map((v) => (v === item ? { ...v, checked } : v));
    this.valueItems.set(updated);
    const allUpdated = this.allValueItems().map((v) => (v.raw === item.raw ? { ...v, checked } : v));
    this.allValueItems.set(allUpdated);
    this.updateSelectAllState();
    this.cdr.markForCheck();
  }

  applyFilter(): void {
    const binding = this.column().binding!;
    const checkedValues = new Set<unknown>();
    for (const item of this.allValueItems()) {
      if (item.checked) {
        checkedValues.add(item.raw);
      }
    }
    const allChecked = checkedValues.size === this.allValueItems().length;
    this.filterApply.emit({
      binding,
      sortDirection: this.filterState()?.sortDirection ?? null,
      checkedValues,
      active: !allChecked,
    });
    this.close();
  }

  clearFilter(): void {
    const cleared = this.allValueItems().map((item) => ({ ...item, checked: true }));
    this.allValueItems.set(cleared);
    this.valueItems.set([...cleared]);
    this.selectAllChecked.set(true);
    this.filterClear.emit(this.column().binding!);
    this.close();
  }

  cancelFilter(): void {
    this.close();
  }

  private computePanelPosition(): void {
    const btn = this.triggerRef()?.nativeElement;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    this.panelTop.set(rect.bottom + 2);
    let left = rect.left;

    const viewportW = window.innerWidth;
    const panelWidth = 260;
    if (left + panelWidth > viewportW) {
      left = Math.max(4, viewportW - panelWidth - 8);
    }
    this.panelLeft.set(left);
  }

  /** Supports `binding` paths like `current_account.description` for nested row objects. */
  private nestedValue(row: unknown, binding: string): unknown {
    if (row == null || typeof row !== 'object') {
      return undefined;
    }
    if (!binding.includes('.')) {
      return (row as Record<string, unknown>)[binding];
    }
    return binding.split('.').reduce<unknown>((acc, key) => {
      if (acc == null || typeof acc !== 'object') {
        return undefined;
      }
      return (acc as Record<string, unknown>)[key];
    }, row);
  }

  private buildValueList(): void {
    const col = this.column();
    const binding = col.binding;
    if (!binding) return;

    const rows = this.data();
    const state = this.filterState();
    const isDate = col.format === 'date';
    const isDateTime = col.format === 'date-time';
    const isTranslate = col.translate === true;

    const uniqueMap = new Map<unknown, string>();
    for (const row of rows) {
      const raw = this.nestedValue(row, binding);
      if (raw == null) continue;
      if (uniqueMap.has(raw)) continue;

      let caption: string;
      if (isDate) {
        caption = formatKlDateCaption(raw);
      } else if (isDateTime) {
        caption = formatKlDateTimeCaption(raw);
      } else if (isTranslate && raw !== '') {
        caption = this.translate.instant(String(raw));
      } else {
        caption = String(raw);
      }
      uniqueMap.set(raw, caption);
    }

    const isChecked = (raw: unknown): boolean => {
      if (!state || !state.active) return true;
      return state.checkedValues.has(raw);
    };

    const items = Array.from(uniqueMap.entries()).map(([raw, caption]) => ({
      raw,
      caption,
      checked: isChecked(raw),
    }));

    items.sort((a, b) => a.caption.localeCompare(b.caption));
    this.allValueItems.set(items);
    this.valueItems.set([...items]);
    this.updateSelectAllState();
  }

  private updateSelectAllState(): void {
    const items = this.valueItems();
    const allChecked = items.length > 0 && items.every((v) => v.checked);
    this.selectAllChecked.set(allChecked);
  }
}
